// Aircraft flight model (AVI-2, E-28). PURE + deterministic (ENG-1): no three /
// DOM / audio / Math.random, so it ticks identically on every peer and replays
// under REP (M33). A deliberately simple, readable arcade model (PLAN §4 — not
// FAA-grade): thrust along the nose, quadratic parasitic drag, wing lift ∝ v²·CL(AoA)
// along the body-up axis (so a bank turns the aeroplane), gravity, a stall break
// past AVI_AOA_STALL, a wheels-on-ground mode below AVI_GEAR_H, and swept-AABB
// collision that tells a hard impact (crash) from a gentle touchdown (landing).
import { AVI } from '../config';

/** A 3-vector (blocks; +x east, +y up, +z south — the player frame). */
export interface Vec3 { x: number; y: number; z: number; }

/** Steering the pilot (or the autopilot, AVI-3) feeds the aircraft each tick.
 *  pitch/roll/yaw are −1..1 control-surface deflections; throttle is held up/down. */
export interface AircraftInput {
  pitch?: number;
  roll?: number;
  yaw?: number;
  throttleUp?: boolean;
  throttleDown?: boolean;
  /** Wheel brake (ground only). */
  brake?: boolean;
}

/** What the flight model needs from the world (ENG-1 keeps it an interface). */
export interface AircraftWorld {
  isSolid(x: number, y: number, z: number): boolean;
}

export interface Aircraft {
  readonly id: number;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  /** Heading (rad, 0 = −z, matching player yaw), pitch (nose up +), roll (right +). */
  yaw: number; pitch: number; roll: number;
  throttle: number; // 0..1
  onGround: boolean;
  /** Set the tick a hard impact wrote it (E-28); the host resets + damages, then clears. */
  crashed: boolean;
}

// ── vector helpers ───────────────────────────────────────────────────────────
function rotate(v: Vec3, k: Vec3, a: number): Vec3 {
  const c = Math.cos(a), s = Math.sin(a);
  const kv = k.x * v.x + k.y * v.y + k.z * v.z; // k·v
  const cx = k.y * v.z - k.z * v.y, cy = k.z * v.x - k.x * v.z, cz = k.x * v.y - k.y * v.x; // k×v
  return {
    x: v.x * c + cx * s + k.x * kv * (1 - c),
    y: v.y * c + cy * s + k.y * kv * (1 - c),
    z: v.z * c + cz * s + k.z * kv * (1 - c),
  };
}
const dot = (a: Vec3, b: Vec3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const len = (a: Vec3): number => Math.hypot(a.x, a.y, a.z);

/** Body axes (forward = nose, up = canopy) from intrinsic yaw→pitch→roll, starting
 *  from forward −z / up +y (the player frame). Exported for the HUD + tests. */
export function bodyAxes(yaw: number, pitch: number, roll: number): { forward: Vec3; up: Vec3; right: Vec3 } {
  let forward: Vec3 = { x: 0, y: 0, z: -1 };
  let up: Vec3 = { x: 0, y: 1, z: 0 };
  let right: Vec3 = { x: 1, y: 0, z: 0 };
  const worldUp: Vec3 = { x: 0, y: 1, z: 0 };
  forward = rotate(forward, worldUp, yaw); right = rotate(right, worldUp, yaw);           // yaw about world up
  forward = rotate(forward, right, pitch); up = rotate(up, right, pitch);                 // pitch about new right
  up = rotate(up, forward, roll); right = rotate(right, forward, roll);                   // roll about the nose
  return { forward, up, right };
}

/** Lift coefficient vs angle of attack (radians): linear until the stall break,
 *  then it collapses to a small residual (a stalled wing still bites a little). */
export function coeffLift(aoa: number): number {
  const peak = AVI.CL_SLOPE * AVI.AOA_STALL;
  if (Math.abs(aoa) <= AVI.AOA_STALL) return AVI.CL_SLOPE * aoa;
  return Math.sign(aoa) * peak * AVI.CL_STALL_RESIDUAL;
}

/** Highest solid block-top at/below `y` under column (x,z), or −Infinity if none
 *  within a few blocks (used for the wheels-on-ground test). */
function groundTopUnder(world: AircraftWorld, x: number, y: number, z: number): number {
  const fx = Math.floor(x), fz = Math.floor(z), top = Math.floor(y);
  for (let cy = top; cy >= top - 5; cy--) if (world.isSolid(fx, cy, fz)) return cy + 1;
  return -Infinity;
}

/** True if the aircraft AABB centred at (x,y,z) overlaps any solid block. */
function blocked(world: AircraftWorld, x: number, y: number, z: number): boolean {
  const h = AVI.HALF, hy = AVI.HALF_H;
  for (let dx = -h; dx <= h; dx += h) for (let dz = -h; dz <= h; dz += h) for (let dy = -hy; dy <= hy; dy += hy) {
    if (world.isSolid(Math.floor(x + dx), Math.floor(y + dy), Math.floor(z + dz))) return true;
  }
  return false;
}

export class AircraftSystem {
  readonly aircraft: Aircraft[] = [];
  private nextId = 1;

  get count(): number { return this.aircraft.length; }

  spawn(x: number, y: number, z: number, yaw = 0): Aircraft {
    const a: Aircraft = { id: this.nextId++, x, y, z, vx: 0, vy: 0, vz: 0, yaw, pitch: 0, roll: 0, throttle: 0, onGround: true, crashed: false };
    this.aircraft.push(a);
    return a;
  }

  remove(a: Aircraft): void { const i = this.aircraft.indexOf(a); if (i >= 0) this.aircraft.splice(i, 1); }
  clear(): void { this.aircraft.length = 0; }

  tick(world: AircraftWorld, inputFor: (a: Aircraft) => AircraftInput = () => ({})): void {
    for (const a of this.aircraft) stepAircraft(a, inputFor(a), world);
  }
}

/** Advance one aircraft one tick (AVI-2). Exported for focused unit tests. */
export function stepAircraft(a: Aircraft, input: AircraftInput, world: AircraftWorld): void {
  // ── controls ──────────────────────────────────────────────────────────────
  a.throttle = Math.max(0, Math.min(1, a.throttle + (input.throttleUp ? AVI.THROTTLE_RATE : 0) - (input.throttleDown ? AVI.THROTTLE_RATE : 0)));
  // Pitch is a trimmed attitude: it holds where the pilot leaves it (hands-off
  // altitude hold), unlike roll which auto-levels the wings for stability.
  a.pitch += (input.pitch ?? 0) * AVI.PITCH_RATE;
  a.roll += (input.roll ?? 0) * AVI.ROLL_RATE;
  a.yaw += (input.yaw ?? 0) * AVI.YAW_RATE;
  if (!input.roll) a.roll *= AVI.CONTROL_DAMP; // release the stick → wings level
  a.roll = Math.max(-AVI.ROLL_MAX, Math.min(AVI.ROLL_MAX, a.roll)); // bank limit, no spiral
  // Pitch limit: a trimmed attitude that holds a strong climb/dive but can't run away
  // to vertical, where the wing stalls and the plane hangs and sinks (AVI, feel fix).
  a.pitch = Math.max(-AVI.PITCH_LIMIT, Math.min(AVI.PITCH_LIMIT, a.pitch));

  const { forward, up } = bodyAxes(a.yaw, a.pitch, a.roll);
  const vel: Vec3 = { x: a.vx, y: a.vy, z: a.vz };
  const speed = len(vel);

  // ── forces (accelerations; mass = 1 so force == accel scaled by 1/MASS) ─────
  let ax = 0, ay = 0, az = 0;
  // Thrust along the nose.
  const thrust = AVI.THRUST_MAX * a.throttle / AVI.MASS;
  ax += forward.x * thrust; ay += forward.y * thrust; az += forward.z * thrust;
  // Angle of attack = angle of the relative wind below the nose (0 in still air).
  const vf = speed > 1e-6 ? dot(vel, forward) : 0;
  const vu = speed > 1e-6 ? dot(vel, up) : 0;
  const aoa = speed > 1e-6 ? Math.atan2(-vu, Math.abs(vf)) : 0;
  // Lift ∝ v²·CL(AoA) along the body-up axis (a bank tilts it → the plane turns).
  const liftMag = AVI.LIFT_K * speed * speed * coeffLift(aoa) / AVI.MASS;
  ax += up.x * liftMag; ay += up.y * liftMag; az += up.z * liftMag;
  // Parasitic drag opposing velocity.
  if (speed > 1e-6) {
    const dragMag = AVI.DRAG_K * speed * speed / AVI.MASS;
    ax -= (vel.x / speed) * dragMag; ay -= (vel.y / speed) * dragMag; az -= (vel.z / speed) * dragMag;
  }
  // Gravity.
  ay -= AVI.GRAVITY;

  a.vx += ax; a.vy += ay; a.vz += az;

  // Weathervane: ease the nose toward the flight-path heading so a banked turn is
  // coordinated (the aircraft turns with the curving velocity, not just slips).
  const hs = Math.hypot(a.vx, a.vz);
  if (hs > 0.5) {
    const velYaw = Math.atan2(-a.vx, -a.vz); // heading of horizontal velocity (yaw frame)
    let d = velYaw - a.yaw;
    d = Math.atan2(Math.sin(d), Math.cos(d)); // shortest signed angle
    a.yaw += d * AVI.WEATHERVANE;
  }

  // ── ground / wheels mode (below GEAR_H over solid ground) ───────────────────
  const groundTop = groundTopUnder(world, a.x, a.y + AVI.HALF_H, a.z);
  const agl = a.y - AVI.HALF_H - groundTop; // wheel height above the surface
  a.onGround = Number.isFinite(groundTop) && agl <= AVI.GEAR_H;
  if (a.onGround) {
    // A fast sink onto the ground is a crash (caught before the wheels absorb it, E-28).
    if (a.vy < -AVI.CRASH_SPEED) a.crashed = true;
    // Roll friction + optional brakes; the wing keeps trying to fly (takeoff when
    // lift ≥ weight makes vy climb on its own).
    const fric = input.brake ? AVI.GROUND_FRICTION * 0.85 : AVI.GROUND_FRICTION;
    a.vx *= fric; a.vz *= fric;
    a.roll *= 0.7; // wheels level the aircraft
    if (a.vy < 0) a.vy = 0; // wheels stop the sink
  }

  // ── swept-AABB integration with crash/land discrimination (E-28) ────────────
  let impact = 0;
  impact = Math.max(impact, moveAxis(a, world, 'x', a.vx));
  impact = Math.max(impact, moveAxis(a, world, 'z', a.vz));
  impact = Math.max(impact, moveAxis(a, world, 'y', a.vy));
  // Rest the wheels exactly on the surface when close, so the plane sits/rolls.
  if (a.onGround && a.vy <= 0) { a.y = groundTop + AVI.HALF_H; a.vy = 0; }
  if (impact > AVI.CRASH_SPEED) a.crashed = true; // a hard contact — the host resets it
}

/** Move one axis, blocked by solids; returns the impact speed if it hit something
 *  (0 otherwise) so the caller can tell a crash from a gentle touchdown (E-28). */
function moveAxis(a: Aircraft, world: AircraftWorld, axis: 'x' | 'y' | 'z', vel: number): number {
  if (vel === 0) return 0;
  const nx = axis === 'x' ? a.x + vel : a.x;
  const ny = axis === 'y' ? a.y + vel : a.y;
  const nz = axis === 'z' ? a.z + vel : a.z;
  if (blocked(world, nx, ny, nz)) {
    a[axis === 'x' ? 'vx' : axis === 'y' ? 'vy' : 'vz'] = 0;
    return Math.abs(vel);
  }
  a[axis] = axis === 'x' ? nx : axis === 'y' ? ny : nz;
  return 0;
}
